import { Input, Select, Typography } from "antd";
import { useDiagramNodes } from "components/CC/context/DiagramNodesContext.tsx";
import { useSelectedNode } from "components/CC/hooks/useDiagramActions";
import { FormValueSync } from "components/CC/UI/FormValueSync";
import { GeneralFormProvider } from "components/UI/GeneralForm/GeneralFormProvider.tsx";
import { useGeneralForm } from "components/UI/GeneralForm/hooks/useGeneralForm.ts";
import { usePaldockTranslation } from "hooks/usePaldockTranslation.tsx";
import { useEffect } from "react";
import { FormItem } from "react-hook-form-antd";
import { z } from "zod";

const mirrorFormSchema = z.object({
    active: z.boolean(),
    title: z.string(),
    subtitle: z.string(),
    mirrorElement: z.string(),
});

export type MirrorFormData = z.infer<typeof mirrorFormSchema>;

interface FormProps {
    onChange?: (data: MirrorFormData) => void;
    onRegisterValidator?: (fn: () => Promise<boolean>) => void;
    formId: string;
}

export const MirrorForm = ({ onChange, onRegisterValidator, formId }: FormProps) => {
    const { selectedNode } = useSelectedNode();
    const defaultValues = { active: true, ...(selectedNode?.formData as MirrorFormData | undefined) } as MirrorFormData;
    return (
        <GeneralFormProvider<MirrorFormData>
            key={selectedNode?.id}
            id={formId}
            schema={mirrorFormSchema}
            defaultValues={defaultValues}
        >
            <FormContent
                onChange={onChange}
                onRegisterValidator={onRegisterValidator}
                currentNodeId={selectedNode?.id}
            />
        </GeneralFormProvider>
    );
};

// Convert camelCase to snake_case for translation keys
const toSnakeCase = (str: string): string => str.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);

const FormContent = ({
    onChange,
    onRegisterValidator,
    currentNodeId,
}: {
    onChange?: (data: MirrorFormData) => void;
    onRegisterValidator?: (fn: () => Promise<boolean>) => void;
    currentNodeId?: string;
}) => {
    const { control, trigger } = useGeneralForm<MirrorFormData>();

    useEffect(() => {
        onRegisterValidator?.(() => trigger());
    }, [onRegisterValidator, trigger]);
    const { t } = usePaldockTranslation("components.connection-creator");
    const { t: tNodes } = usePaldockTranslation("components.connection-creator");
    const { nodeOptions } = useDiagramNodes();

    // Get current node's actual flow number from nodeOptions (based on graph connectivity)
    const currentNode = nodeOptions.find((n) => n.id === currentNodeId);
    const currentFlowNumber = currentNode?.flowNumber ?? 0;

    // Check if node is orphan (flow 0 = not connected to any flow)
    const isOrphanNode = currentFlowNumber === 0;

    // Check if there are multiple flows (to decide whether to show flow prefix)
    const uniqueFlows = new Set(nodeOptions.map((n) => n.flowNumber).filter((f) => f > 0));
    const hasMultipleFlows = uniqueFlows.size > 1;

    // Build options from all available nodes with their sort order
    const elementOptions = nodeOptions
        .filter((node) => node.flowNumber > 0) // Exclude orphan nodes from options
        .sort((a, b) => {
            // Sort by flow number first, then by sort order
            if (a.flowNumber !== b.flowNumber) return a.flowNumber - b.flowNumber;
            return (a.sortOrder ?? 0) - (b.sortOrder ?? 0);
        })
        .map((node) => {
            // Convert node type to translation key: "subScenarioNode" -> "sub_scenario"
            const nodeTypeKey = toSnakeCase(node.type.replace("Node", ""));
            const nodeTitle = node.label || tNodes(`nodes.${nodeTypeKey}.title`);

            // Build label: include flow type prefix if multiple flows exist
            let label: string;
            if (hasMultipleFlows && node.flowType) {
                const flowLabel = t(`enums.flow_types.${node.flowType}`);
                label = `${flowLabel} - ${node.sortOrder ?? "?"} - ${nodeTitle}`;
            } else {
                label = `${node.sortOrder ?? "?"} - ${nodeTitle}`;
            }

            return {
                value: node.id,
                label,
            };
        });

    return (
        <div className="bg-white rounded-lg p-4 m-3">
            <FormValueSync<MirrorFormData> onChange={onChange} />
            {/* Header with title and active toggle */}
            <div className="flex justify-between items-center mb-4">
                <Typography.Title level={4} className="!mb-0">
                    {t("nodes.mirror.forms.settings")}
                </Typography.Title>
                {/* Active toggle — hidden until BE is_enabled is wired up
                <Controller
                    control={control}
                    name="active"
                    render={({ field }) => (
                        <div className="flex items-center gap-2">
                            <Switch checked={field.value} onChange={field.onChange} />
                            <Typography.Text type="secondary">{t("nodes.mirror.forms.active")}</Typography.Text>
                        </div>
                    )}
                /> */}
            </div>

            {/* Title and Subtitle row */}
            <div className="flex gap-4 mb-4">
                <FormItem className="flex-1 !mb-0" control={control} name="title" label={t("nodes.mirror.forms.title")}>
                    <Input placeholder={t("nodes.mirror.forms.title_placeholder")} />
                </FormItem>
                <FormItem
                    className="flex-1 !mb-0"
                    control={control}
                    name="subtitle"
                    label={t("nodes.mirror.forms.subtitle")}
                >
                    <Input placeholder={t("nodes.mirror.forms.subtitle_placeholder")} />
                </FormItem>
            </div>

            {/* Mirror Element dropdown */}
            <FormItem
                className="!mb-0"
                control={control}
                name="mirrorElement"
                label={t("nodes.mirror.forms.mirror_element")}
            >
                <Select
                    placeholder={
                        isOrphanNode
                            ? t("nodes.mirror.forms.mirror_element_disabled")
                            : t("nodes.mirror.forms.mirror_element_placeholder")
                    }
                    options={elementOptions}
                    allowClear
                    disabled={isOrphanNode}
                />
            </FormItem>
        </div>
    );
};
